/**
 * Public hook: fetch a curated page by slug with its items.
 * Only returns pages with status='public'.
 * SoT: docs/experiments/curated-pages-v1.md
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CuratedPageItem {
  id: string;
  sort_order: number;
  day_label: string | null;
  narrative_title: string;
  narrative_body: string | null;
  entity_type: string;
  entity_id: string;
  cta_label: string | null;
  is_primary: boolean;
}

export interface CuratedPageData {
  id: string;
  slug: string;
  title: string;
  intro: string;
  og_description: string | null;
  status: string;
  duration_label: string | null;
  hero_media_asset_id: string | null;
  items: CuratedPageItem[];
}

export function useCuratedPage(slug: string | undefined) {
  return useQuery({
    queryKey: ["curated-page", slug],
    enabled: !!slug,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CuratedPageData | null> => {
      if (!slug) return null;

      const { data: page, error: pageError } = await supabase
        .from("curated_pages")
        .select("id, slug, title, intro, og_description, status, duration_label, hero_media_asset_id")
        .eq("slug", slug)
        .eq("status", "public")
        .maybeSingle();

      if (pageError) throw pageError;
      if (!page) return null;

      const { data: items, error: itemsError } = await supabase
        .from("curated_page_items")
        .select("id, sort_order, day_label, narrative_title, narrative_body, entity_type, entity_id, cta_label, is_primary")
        .eq("curated_page_id", page.id)
        .order("sort_order", { ascending: true });

      if (itemsError) throw itemsError;

      return {
        ...page,
        items: items ?? [],
      };
    },
  });
}
